import React, { useEffect, useRef } from 'react';
import { Empty, Spin } from 'antd';
import ApexCharts from 'apexcharts';
import { getCurrency } from '../Common/utils';
import { ChargePoint } from './useChargeStatistics';

/** the dashboard's chart palette, so a graph looks the same wherever it is */
const INK = '#103075ff';
const ACCENT = '#559fb8ff';

/** roughly the room one date label needs before it runs into its neighbour */
const LABEL_SLOT = 88;

/**
 * Thin the axis labels to what the axis can actually hold: every one while
 * there are few, every Nth once there are more than fit. The points keep their
 * places — only the labels between the kept ones are blanked — so the line is
 * unchanged and just stops being written over.
 *
 * Counting back from the end rather than forward from the start keeps the
 * spacing even and always labels the most recent period, which is the end the
 * chart is read from.
 */
export const thinLabels = (labels: string[], width: number) => {
    const fits = Math.max(2, Math.floor(width / LABEL_SLOT));
    if (labels.length <= fits) return labels;

    const stride = Math.ceil(labels.length / fits);
    const last = labels.length - 1;
    return labels.map((label, index) => ((last - index) % stride === 0 ? label : ''));
};

/**
 * Charge over time, drawn the way the dashboard draws its metrics — same
 * library, palette, grid and legend — so the two pages read as one product.
 *
 * Presentational: the series is fetched once by the page and handed to this
 * and to the summary beside it.
 */
const ChargeChart = ({ points, loading }: { points: ChargePoint[]; loading?: boolean }) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const currency = getCurrency();

    const hasData = !loading && points.length > 0;

    useEffect(() => {
        if (!chartRef.current || !hasData) return undefined;

        if (chartInstance.current) chartInstance.current.destroy();

        const money = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        });
        const moneyExact = new Intl.NumberFormat(undefined, { style: 'currency', currency });

        const categories = points.map((point) =>
            point.at.toLocaleDateString('en-UK', { month: 'short', year: 'numeric' }),
        );

        // how many labels fit depends on how wide the panel is, and the panel
        // shares its row with the summary — so the width is measured, not assumed
        const width = chartRef.current.clientWidth || 720;

        const options = {
            chart: {
                height: 300,
                type: 'area' as const,
                fontFamily: 'Inter, sans-serif',
                dropShadow: { enabled: false },
                toolbar: { show: true },
                zoom: { enabled: false },
                // ApexCharts otherwise writes min-height: height + 15px onto
                // the element it is given, which is 15px of blank below a chart
                // that already fits — the panel provides its own spacing
                parentHeightOffset: 0,
            },
            series: [{ name: 'Total charge', data: points.map((point) => point.total) }],
            colors: [INK],
            stroke: { width: 3, curve: 'smooth' as const },
            fill: {
                type: 'gradient',
                gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] },
            },
            dataLabels: { enabled: false },
            markers: {
                size: 4,
                colors: [INK],
                strokeColors: '#fff',
                strokeWidth: 2,
                hover: { sizeOffset: 2 },
            },
            legend: { show: true, position: 'top' as const, horizontalAlign: 'left' as const },
            tooltip: {
                enabled: true,
                marker: { show: false },
                // the axis only writes some of the dates; the tooltip reads the
                // full list, so a point whose label was thinned away still says
                // which period it is
                x: {
                    formatter: (value: any, opts: any) =>
                        categories[opts?.dataPointIndex] ?? value,
                },
                y: { formatter: (value: number) => moneyExact.format(value || 0) },
            },
            xaxis: {
                type: 'category' as const,
                categories: thinLabels(categories, width),
                labels: {
                    style: {
                        fontFamily: 'Inter, sans-serif',
                        cssClass: 'text-xs font-normal fill-gray-500',
                    },
                    // there are few enough of them now to sit flat, which is
                    // easier to read than the angle crowding forces
                    rotate: 0,
                    rotateAlways: false,
                    hideOverlappingLabels: true,
                    // A label is sized against the slot one category gets, and
                    // with the labels thinned most categories are blank — so
                    // trimming would cut "Feb 2023" down to "Fe…" to fit a slot
                    // whose neighbours are empty. The thinning is what keeps
                    // them apart; nothing needs to be cut.
                    trim: false,
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false },
                tickPlacement: 'on' as const,
            },
            yaxis: {
                labels: {
                    style: { colors: [ACCENT] },
                    formatter: (value: number) => money.format(value || 0),
                    offsetX: -6,
                },
                min: 0,
                tickAmount: 5,
                forceNiceScale: true,
            },
            grid: {
                show: true,
                borderColor: '#e1e8ed',
                strokeDashArray: 1,
                position: 'back' as const,
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: true } },
                padding: { left: 14, right: 14, top: 6, bottom: 0 },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        // the chart shares a row with the summary, so its width changes with
        // the viewport rather than only on mount
        let resizeTimeout: NodeJS.Timeout;
        const observer = new ResizeObserver(() => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (chartInstance.current && chartRef.current) {
                    const next = chartRef.current.clientWidth;
                    chartInstance.current.updateOptions(
                        {
                            chart: { width: next },
                            // a narrower axis holds fewer labels, so the
                            // thinning is redone rather than left at whatever
                            // the first render worked out
                            xaxis: { categories: thinLabels(categories, next) },
                        },
                        false,
                        false,
                    );
                }
            }, 10);
        });
        observer.observe(chartRef.current);

        return () => {
            clearTimeout(resizeTimeout);
            observer.disconnect();
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
        };
    }, [points, hasData, currency]);

    return (
        <div className="chg-chart">
            {/*
              ApexCharts owns every node inside this div, so React must never be
              asked to unmount it: returning a spinner *instead of* it makes
              React remove children ApexCharts has already replaced or torn
              down, and the commit throws NotFoundError. The states below sit
              over the container rather than taking its place.
            */}
            <div ref={chartRef} className="chg-chart__canvas" aria-hidden={!hasData} />

            {!hasData && (
                <div className="chg-chart__state">
                    {loading ? (
                        <Spin />
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No charges in this period" />
                    )}
                </div>
            )}
        </div>
    );
};

export default ChargeChart;
